/**
 * interfaces
 */
import { IUnitModel } from './interfaces';
import { IUnitDto, IUnitInlineTypeDto } from '../../interfaces';

/**
 * base model
 */
import { UnitBaseModel } from '../unit-base-model';

/**
 * View model for a first-level unit (GET units/first_level).
 *
 * Extends {@link UnitBaseModel} — all common getters and {@link hasChildrenBadge}
 * are inherited. This class only adds the fields specific to {@link IUnitDto}:
 * the full inline type object, icon, and facility reference.
 *
 * Each model declares its own named DTO (`unitDto`) for clear, type-safe
 * access to its shape-specific fields.
 *
 * @example
 * ```ts
 * const model = new UnitModel(dto);
 * console.log(model.name);                   // "Zone 01"  (base getter)
 * console.log(model.unitDto.type.name);      // "Floor"    (own DTO)
 * console.log(model.hasChildrenBadge.label); // "GENERAL.YES"
 * ```
 */
export class UnitModel<TDto extends IUnitDto = IUnitDto> extends UnitBaseModel<TDto> implements IUnitModel<TDto> {

    /**
     * @param unitDto - Raw first-level unit DTO from the API.
     */
    constructor(public readonly unitDto: TDto) {
        super(unitDto);
    }

    // ─── IUnitDto-specific getters ────────────────────────────────────────────

    /**
     * Full inline type object, including the global_type flag.
     */
    public get type(): IUnitInlineTypeDto {
        return this.unitDto.type;
    }

    /**
     * Icon identifier string, or null if not set.
     */
    public get icon(): string | null {
        return this.unitDto.icon;
    }

    /**
     * Parent facility ID reference, or null.
     */
    public get facility(): string | null {
        return this.unitDto.facility;
    }

}
